import {
  DocumentNode,
  MutationHookOptions,
  MutationResult,
  OperationVariables,
  useMutation,
  useQuery,
} from '@apollo/client';
import { client } from '../client';
import normalizeVariables from '../graphql/schema/normalizeVariables';
import { serviceActions } from '../state/modules/service';
import { appActions } from '../state/modules/app';
import isPromise from '../utils/isPromise';
import { handleAlerts, parseErrorResponse, useComponentQueryErrorHandling } from './parseErrorResponse';
import setIn from '../components/connectedForm/utils/setIn';
import { useDispatch, useSelector } from 'react-redux';
import { AlertType, IJobInfo } from '../graphql/schema/p4types';
import { Dispatch } from 'redux';
import { useCallback, useMemo, useRef, useState } from 'react';
import { IReduxStore } from '../@types/reduxTypes';
import { Maybe } from 'graphql/jsutils/Maybe';
import CheckJobStatusQuery from '../graphql/queries/system/CheckJobStatusQuery.graphql';
import { EntityType } from '../@types/entityTypes';
import { useOnProcessSummary } from '../components/modals/DocumentModal2';
import { onDocumentComplete } from './DocumentUtils';
import { useP4ToastController } from '../views/system/NotificationsArea';

function alertMessage(title: string, message: string, type: AlertType, dispatch: Dispatch) {
  if (!dispatch) console.error('Dispatch not set for notification', title, message);
  dispatch(serviceActions.genericAlert(title, message, type));
}

export function callMutation(mutation: DocumentNode, input: any, backgroundJob: boolean) {
  let jobDetails;
  const variables = normalizeVariables(input);
  let response;
  try {
    response = client.mutate({
      mutation: mutation,
      variables: { input: variables },
    });
  } catch (e: any) {
    console.error('Job could not be started', e);
    return { error: e.message };
  }
  if (isPromise(response)) {
    return response.then(
      (response) => {
        const { data } = response;
        if (data && backgroundJob) {
          const result = Object.keys(data)[0];
          jobDetails = data[result] && data[result].result ? data[result].result : {};
        } else {
          jobDetails = data;
        }
        return jobDetails;
      },
      (error) => {
        return {
          error,
        };
      }
    );
  }
  return jobDetails;
}

/**
 * @deprecated - Should typically use 'useBackgroundMutation' (Or 'useBackgroundProcess' in some rare circumstances).
 */
export function createBackgroundJob<TInput>(
  jobMutation: DocumentNode,
  input: TInput,
  {
    dispatch,
    blockNavigation,
    connectedForm,
    afterSubmit,
    history,
  }: {
    dispatch: Dispatch;
    blockNavigation?: boolean;
    connectedForm: any;
    afterSubmit?: (result?: any) => void;
    history: any;
  },
  returnUrl: string | undefined | null,
  title: string,
  onComplete: (job: IJobMeta) => void,
  onFailed?: (job?: IJobMeta) => void
) {
  const result = callMutation(jobMutation, input, true);
  (result as Maybe<Promise<any>>)?.then?.((job: IJobMeta) => {
    if (job && job.jobId && !job.failed) {
      job.title = title;
      job.onComplete = onComplete;
      job.onFailed = onFailed;
      dispatch(appActions.startJob(job));
      alertMessage(title, 'Job started', AlertType.Info, dispatch);
    } else {
      alertMessage(
        'Job Error',
        `Job could not be started${job && job.message ? ` - ${job.message}` : ''}`,
        AlertType.Error,
        dispatch
      );
      onFailed?.(job);
    }
  });
  if (blockNavigation) return;
  if (returnUrl === 'back') {
    if (connectedForm) {
      connectedForm.afterSubmit(result);
    } else if (afterSubmit) {
      afterSubmit(result);
    } else {
      history.goBack();
    }
  } else if (returnUrl && connectedForm) {
    connectedForm.history.push({
      pathname: returnUrl,
      state: { previousLocation: connectedForm.history.location?.pathname || 'back' },
    });
  } else if (returnUrl && history) {
    history.push({
      pathname: returnUrl,
      state: { previousLocation: history.location?.pathname || 'back' },
    });
  }
}

export function createBackgroundProcess(
  mutation: DocumentNode,
  input: any,
  props: any,
  returnUrl: string | null,
  message: string,
  update?: any
) {
  const formProps = props && props.connectedForm ? props.connectedForm : props;
  const { history, dispatch, entity } = formProps;
  const result = callMutation(mutation, input, false);
  const mutationProps = {
    mutation: mutation,
    originalInput: input,
    backgroundJob: false,
    responseProps: { props, history, dispatch, entity, returnUrl, message, update },
  };
  (result as Maybe<Promise<any>>)?.then?.((result) => {
    handleResponse(result, props, history, dispatch, entity, returnUrl, message, update, undefined, mutationProps);
  });
}

/**
 * Function to re-fire a mutation off the back of a confirmation prompt (That is not the typical 'save' mutation, i.e. post invoice for purchase invoices).
 * @param confirmationPrompt - Confirmation prompt props. 'mutationProps' and 'confirmationReference' are required on this object.
 */
export function reProcess({
  mutationProps,
  confirmationReference,
}: {
  mutationProps: any;
  confirmationReference: string | undefined;
}) {
  const { mutation, originalInput, backgroundJob, responseProps } = mutationProps;

  //If we're provided mutation props, re-fire the mutation with the confirmation attached.
  const field = `confirmationResponses.${[confirmationReference]}`;
  const newInput = setIn(originalInput, field, true);
  const result = callMutation(mutation, newInput, backgroundJob);
  (result as Maybe<Promise<any>>)?.then?.((result) => {
    const { props, history, dispatch, entity, returnUrl, message, update } = responseProps;
    handleResponse(result, props, history, dispatch, entity, returnUrl, message, update, undefined, {
      mutation,
      originalInput: newInput,
      backgroundJob,
      responseProps,
    });
  });
}

//Separated from createBackgroundProcess to allow usage with <Mutation/> components.
export function handleResponse(
  result: any,
  props: any,
  history: any,
  dispatch: Dispatch,
  entity: Maybe<EntityType>,
  returnUrl?: Maybe<string>,
  message?: string,
  update?: (jobDetails?: any, props?: any) => void,
  returnData?: any,
  mutationProps?: any
) {
  const isError = result.error || result.networkError;

  const navigateBack = () => {
    if (returnUrl) {
      if (returnUrl === 'back') {
        if (props.connectedForm) {
          props.connectedForm.afterSubmit(result);
        } else if (props.afterSubmit) {
          props.afterSubmit(result);
        } else {
          history.goBack();
        }
      } else {
        history.push({ pathname: returnUrl, state: { previousLocation: history.location?.pathname || 'back' } });
      }
    }
  };

  if (result && !isError) {
    if (message) alertMessage(`${message}`, `${message} completed successfully`, AlertType.Success, dispatch);
    navigateBack();
    if (update && returnData) {
      let jobDetails;
      if (result.data) {
        const jobKey = Object.keys(result.data)[0];
        jobDetails = result.data[jobKey] && result.data[jobKey].result ? result.data[jobKey].result : {};
      } else {
        jobDetails = result.data;
      }
      update(jobDetails);
    } else if (update) {
      update(result, props);
    }
  } else {
    const response = result.error || result;
    let errors = null;
    if (response.networkError && response.networkError.result) {
      errors = response.networkError.result.errors;
    }

    const error = parseErrorResponse(errors);
    const errorProps: Parameters<typeof handleAlerts>[1] = {
      dispatch,
      name: message,
      entity,
      customAlertHandler: props.customAlertHandler,
    };
    handleAlerts(error, errorProps, 'completed', mutationProps);
  }
}

//UI Wrapped of JobInfo with UI-side only additional data (title for notification message, callback for completion, callback for failure).
export type IJobMeta = IJobInfo & {
  title?: string;
  onComplete?: (job: IJobInfo) => void;

  /**
   * Failed callback.
   * Return false if a default "Job Failed" toast should be generated.
   * Return true otherwise (I.e. If the onFailed being provided will generate a similar toast).
   */
  onFailed?: (job?: IJobInfo) => boolean | void;
  backgrounded?: boolean;
};

//Returns a function for creating a background process from a job.
export const useBackgroundProcess = ({
  title,
  onComplete,
  onFailed: overrideOnFailed = undefined,
}: {
  title: string;
  onComplete: (job: IJobMeta) => void;
  onFailed?: (job?: IJobMeta) => boolean | void;
}) => {
  const { dispatchJobToast, dispatchSimpleToast } = useP4ToastController();

  return useCallback(
    (job: IJobMeta) => {
      const onFailed =
        overrideOnFailed ||
        ((j: IJobMeta) => {
          let alertText = j.message ?? '';

          if (j.errorMessages && j.errorMessages.length > 0) {
            alertText += `\n${j.errorMessages.join('\n')}`;
          }
          dispatchSimpleToast('Generation Unsuccessful', alertText, AlertType.Error);
          return true;
        });

      if (job && job.jobId && !job.failed) {
        job.title = title;
        job.onComplete = onComplete;
        job.onFailed = onFailed as any;
        dispatchJobToast(job, { toastId: job.jobId });
      } else {
        dispatchSimpleToast(
          'Job Error',
          `Job could not be started${job && job.message ? ` - ${job.message}` : ''}`,
          AlertType.Error
        );
        onFailed(job);
      }
    },
    [dispatchJobToast, dispatchSimpleToast, onComplete, overrideOnFailed, title]
  );
};

export type BackgroundMutationHookOptions<TData = any, TVariables = OperationVariables> = MutationHookOptions<
  TData,
  TVariables
> & {
  onStartJob?: (job: IJobMeta) => void;
  onCompleteJob: (job: IJobMeta) => void;
  onFailedJob?: (job?: IJobMeta) => void;
  jobTitle?: string;
  errorOptions?: Parameters<typeof useComponentQueryErrorHandling>[0];
};

/**
 * Similar to 'useMutation' from apollo, but handles a JobInfo result and sets it up as a P4 background job with progress notification.
 * @param mutation - GraphQL Mutation to run.
 * @param onStartJob - Called when the initial mutation returns with the job info.
 * @param onCompleteJob - Called when the job is completed.
 * @param onFailedJob - Called if the job fails.
 * @param jobTitle - Job title
 * @param errorOptions - Pass-through options for 'useComponentQueryErrorHandling' (I.e. Custom error name).
 * @param options - Other options as available to a regular "useMutation" call.
 */
export const useBackgroundMutation = <TData = any, TVariables = OperationVariables>(
  mutation: DocumentNode,
  {
    onStartJob,
    onCompleteJob,
    onFailedJob,
    jobTitle = 'Background Job',
    errorOptions,
    ...options
  }: BackgroundMutationHookOptions
) => {
  const onError = useComponentQueryErrorHandling(errorOptions);
  const { dispatchSimpleToast } = useP4ToastController();

  const [inProgress, setInProgress] = useState(false);
  const [currentJobId, setCurrentJobId] = useState<Maybe<string>>();
  const reduxJob = useSelector<IReduxStore, Maybe<IJobMeta>>((store) =>
    !currentJobId ? undefined : store.app.jobMap[currentJobId]
  );
  const lastJob = useRef<Maybe<IJobMeta>>();
  //The job gets removed from the redux store on completion, so just return the latest iteration instead
  // (Which should contain the complete/failed flags & counts/etc as appropriate).
  const currentJob = useMemo(() => {
    if (reduxJob) {
      lastJob.current = reduxJob;
      return reduxJob;
    }
    //Forcing the complete flag "just-in-case" - Covering a concern that the job could be removed from the store before this memo can set 'lastJob.current'
    return { ...lastJob.current, complete: true };
  }, [reduxJob]);
  //Ensure the job continues to be updated if the user dismisses the notification (Since they haven't navigated away, assume they're still waiting on the job).
  useBackgroundJobStatusUpdates(currentJob);

  const onErrorWithFailCall = useCallback(
    (error: Parameters<typeof onError>) => {
      setInProgress(false);
      onError(error);
      onFailedJob?.();
    },
    [onError, onFailedJob]
  );

  const [runMutation, otherMutationProps] = useMutation<TData, TVariables>(mutation, {
    onError: onErrorWithFailCall,
    ...(options as MutationHookOptions<TData, TVariables>),
  });

  const onCompleteWithProgress = useCallback(
    (...args) => {
      setInProgress(false);
      onCompleteJob(...args);
    },
    [onCompleteJob]
  );

  const onFailedWithProgress = useCallback(
    (...args) => {
      setInProgress(false);
      onFailedJob?.(...args);
    },
    [onFailedJob]
  );

  const runBackgroundProcess = useBackgroundProcess({
    title: jobTitle,
    onComplete: onCompleteWithProgress,
    onFailed: onFailedWithProgress,
  });

  const backgroundResponse: [
    (options?: MutationHookOptions<TData, TVariables>) => void,
    MutationResult<TData> & { job: Maybe<IJobMeta>; inProgress: boolean }
  ] = [
    (options: MutationHookOptions<TData, TVariables> = {}) => {
      setInProgress(true);
      runMutation(options)
        .then((result: any) => {
          if (!result) {
            //This error should never be thrown, just here to ensure we get an alert not an error if something did go wrong.
            dispatchSimpleToast('Unable to process job', 'No job was returned from the action', AlertType.Error);
            return;
          }
          const key = Object.keys(result.data)[0];
          const jobMeta: IJobMeta = result.data[key].result;

          setCurrentJobId(jobMeta.jobId);
          onStartJob?.(jobMeta);
          runBackgroundProcess(jobMeta);
        })
        .catch(() => {
          setInProgress(false);
        });
    },
    { ...otherMutationProps, job: currentJob, inProgress },
  ];

  return backgroundResponse;
};

/**
 * If user dismisses the job notification, it will no longer be polling for status updates.
 * Since the results here rely on it, added this logic to take over the task in that scenario.
 */
const useBackgroundJobStatusUpdates = (backgroundJob: Maybe<IJobMeta>) => {
  const dispatch = useDispatch();

  const { dispatchSimpleToast } = useP4ToastController();

  const skip = useMemo(() => {
    return !backgroundJob?.backgrounded || !!backgroundJob?.complete;
  }, [backgroundJob?.backgrounded, backgroundJob?.complete]);

  useQuery(CheckJobStatusQuery, {
    variables: { jobId: backgroundJob?.jobId },
    pollInterval: skip ? 0 : 2500, //If you don't explicitly clear the pollInterval, polling may continue even when skip is true.
    onCompleted: (data) => {
      const job = data?.jobStatus;

      if (job && job.jobId) {
        dispatch(appActions.updateJobStatus(job));
      } else {
        dispatchSimpleToast('Job Status Check', 'Job status timeout - Action may still be running.', AlertType.Error);
      }
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    skip: skip,
  });
};

/**
 * Extension of 'useBackgroundMutation' specifically targeted for use with the 'onPrintClick' prop of DocumentModal2.
 *
 */
export const usePrintMutation = <TData, TVariables = OperationVariables>(
  mutation: DocumentNode,
  options?: Partial<Omit<BackgroundMutationHookOptions, 'onCompleteJob'>> & {
    onCompleteJob: (job: IJobMeta) => boolean | undefined; //If returns true, overrides basic print handling version of 'onDocumentComplete'.
  }
) => {
  const dispatch = useDispatch();
  const showSummary = useRef<boolean>(false);
  const onProcessSummary = useOnProcessSummary();
  const [print, otherMutationProps] = useBackgroundMutation<TData, TVariables>(mutation, {
    ...options,
    onCompleteJob: (job) => {
      if (!options?.onCompleteJob?.(job)) {
        onDocumentComplete?.({
          documentName: 'Stock Take Report',
          dispatch,
          job,
          showSummary: showSummary.current,
          onProcessSummary: () => onProcessSummary(false, { showSummary: showSummary.current }, {}, job),
        });
      }
    },
  });

  const onPrint = (
    options: MutationHookOptions<TData, TVariables> = {},
    { showSummary: shouldShowSummary }: { showSummary?: boolean }
  ) => {
    print(options);
    showSummary.current = !!shouldShowSummary;
  };

  const opts: [
    (options: MutationHookOptions<TData, TVariables>, printOptions: { showSummary?: boolean }) => void,
    MutationResult<TData> & { job: Maybe<IJobMeta> }
  ] = [onPrint, otherMutationProps];
  return opts;
};
